import React, { useEffect, useState } from "react";
import { FormikHelpers, useFormik } from "formik";
import * as Yup from "yup";
import LockIcon from "@mui/icons-material/Lock";
import {
  Box,
  Card,
  CardContent,
  Container,
  Link,
  Stack,
  TextField,
  Typography,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Button,
} from "@mui/material";
import BrandingLogo from "components/branding-logo";
import { motion } from "framer-motion";
import { LoadingButton } from "@mui/lab";
import { MuiTelInput, MuiTelInputCountry } from "mui-tel-input";
import { useSite } from "hooks/use-site";
import { useAppDispatch, useAppSelector } from "store";
import { useLocation, useNavigate } from "react-router-dom";
import { errorNotification } from "../../features/notifications/notificationsSlice";
import { getDefaultKycPrograms, signUpExtended } from "../../service/api";
import { NewUser, setNewUser } from "../../features/register/registerSlice";

type InitialValues = {
  email: string;
  phone: string;
  password: string;
  passwordConfirmation: string;
  terms_agree_check: boolean;
  privacy_agree_check: boolean;
  age_agree_check: boolean;
};

type WalletProgram = {
  id: number;
  wallet_type: "Private Wallet" | "Business Wallet";
  default_currency: string;
  disable_address_fields_in_registration_form: number;
  default_mobile_country_code: string;
};

const validationSchema = Yup.object().shape({
  email: Yup.string().email("Email is invalid").required("Email is required"),
  phone: Yup.string()
    .required("Mobile is required")
    .test("is-valid-phone", "Invalid phone number", (value) => {
      if (!value) return false;
      const cleaned = value.replace(/\D/g, "");
      return cleaned.length >= 10 && cleaned.length <= 15;
    }),
  password: Yup.string()
    .required("Password is required")
    .min(8, "Must be at least 8 characters")
    .matches(/[a-z]/, "Lowercase required")
    .matches(/[A-Z]/, "Uppercase required")
    .matches(/[0-9]/, "Number required"),
  passwordConfirmation: Yup.string().oneOf(
    [Yup.ref("password"), null],
    "Passwords must match"
  ),
  terms_agree_check: Yup.boolean().oneOf([true], "You must agree to terms"),
  privacy_agree_check: Yup.boolean().oneOf(
    [true],
    "You must agree to privacy policy"
  ),
  age_agree_check: Yup.boolean().oneOf([true], "You must confirm age"),
});

const SignUpWithPassword = () => {
  const navigate = useNavigate();
  const { termsAndConditionsUrl, privacyAndPolicyUrl } = useSite();
  const { search } = useLocation();
  const typeParam = new URLSearchParams(search).get("type");
  const desiredType =
    typeParam === "business" ? "Business Wallet" : "Private Wallet";
  const [kycProgramId, setKycProgramId] = useState<number | null>(null);
  const dispatch = useAppDispatch();
  const newUser = useAppSelector((state) => state.register.newUser);
  const [defaultCountry, setDefaultCountry] =
    useState<MuiTelInputCountry>("CZ");

  useEffect(() => {
    getDefaultKycPrograms().then(
      (data: { walletPrograms: WalletProgram[] }) => {
        const prog = data.walletPrograms.find(
          (p: WalletProgram) => p.wallet_type === desiredType
        );
        if (prog) {
          setKycProgramId(prog.id);

          if (prog.default_mobile_country_code) {
            setDefaultCountry(
              prog.default_mobile_country_code as MuiTelInputCountry
            );
          }
        }
      }
    );
  }, [desiredType]);

  const {
    handleSubmit,
    getFieldMeta,
    setFieldValue,
    handleChange,
    handleBlur,
    isValid,
    isSubmitting,
  } = useFormik<InitialValues>({
    initialValues: {
      email: "",
      phone: "",
      password: "",
      passwordConfirmation: "",
      terms_agree_check: false,
      privacy_agree_check: false,
      age_agree_check: false,
    },
    validationSchema,
    onSubmit: async (
      { email, phone, password },
      { setSubmitting }: FormikHelpers<InitialValues>
    ) => {
      setSubmitting(true);
      try {
        if (kycProgramId == null) {
          dispatch(errorNotification("KYC program is not loaded yet"));
          return;
        }

        await signUpExtended({
          email,
          phone,
          password,
          kyc_program_id: kycProgramId,
        });

        dispatch(setNewUser({ ...newUser, email, phone } as NewUser));
        navigate("/registration-completed");
      } catch (err: any) {
        dispatch(errorNotification(err.message || "Registration failed"));
      } finally {
        setSubmitting(false);
      }
    },
  });

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        px: { xs: 1.5, sm: 2 },
        py: { xs: 2, sm: 4 },
      }}
    >
      <Container maxWidth="md" sx={{ px: 0 }}>
        <form onSubmit={handleSubmit}>
          <Card
            elevation={24}
            component={motion.div}
            layoutId="auth-card"
            sx={{ borderRadius: 2, overflow: "hidden" }}
          >
            <Box
              sx={{
                py: { xs: 2, sm: 2.5 },
                px: { xs: 2, sm: 4 },
                bgcolor: "brand.auth",
                display: "flex",
                justifyContent: "center",
              }}
            >
              <BrandingLogo height={56} />
            </Box>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Stack spacing={{ xs: 2.5, sm: 4 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <LockIcon fontSize="large" />
                  <Typography variant="h6" fontWeight={600}>
                    Create Account
                  </Typography>
                </Stack>
                <Stack spacing={2}>
                  <TextField
                    label="Email"
                    name="email"
                    type="email"
                    fullWidth
                    placeholder="you@example.com"
                    InputLabelProps={{ shrink: true }}
                    value={getFieldMeta("email").value}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={
                      !!getFieldMeta("email").error &&
                      getFieldMeta("email").touched
                    }
                    helperText={getFieldMeta("email").error}
                  />
                  <TextField
                    label="Password"
                    name="password"
                    type="password"
                    fullWidth
                    placeholder="At least 8 characters"
                    InputLabelProps={{ shrink: true }}
                    value={getFieldMeta("password").value}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={
                      !!getFieldMeta("password").error &&
                      getFieldMeta("password").touched
                    }
                    helperText={getFieldMeta("password").error}
                  />
                  <TextField
                    label="Retype Password"
                    name="passwordConfirmation"
                    type="password"
                    fullWidth
                    placeholder="Re-enter password"
                    InputLabelProps={{ shrink: true }}
                    value={getFieldMeta("passwordConfirmation").value}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={
                      !!getFieldMeta("passwordConfirmation").error &&
                      getFieldMeta("passwordConfirmation").touched
                    }
                    helperText={getFieldMeta("passwordConfirmation").error}
                  />
                  <MuiTelInput
                    key={defaultCountry}
                    defaultCountry={defaultCountry}
                    fullWidth
                    label="Mobile"
                    name="phone"
                    InputLabelProps={{ shrink: true }}
                    value={getFieldMeta("phone").value}
                    onChange={(val) => setFieldValue("phone", val)}
                    error={
                      !!getFieldMeta("phone").error &&
                      getFieldMeta("phone").touched
                    }
                    helperText={getFieldMeta("phone").error}
                  />
                </Stack>
                <Stack>
                  <FormGroup>
                    <FormControlLabel
                      control={<Checkbox />}
                      label={
                        <Typography component="span">
                          I agree to the{" "}
                          <Link href={termsAndConditionsUrl} target="_blank">
                            terms and conditions
                          </Link>
                        </Typography>
                      }
                      name="terms_agree_check"
                      checked={getFieldMeta("terms_agree_check").value}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                    {getFieldMeta("terms_agree_check").touched &&
                      getFieldMeta("terms_agree_check").error && (
                        <Typography color="error">
                          {getFieldMeta("terms_agree_check").error}
                        </Typography>
                      )}
                  </FormGroup>
                  <FormGroup>
                    <FormControlLabel
                      control={<Checkbox />}
                      label={
                        <Typography component="span">
                          I agree to the{" "}
                          <Link href={privacyAndPolicyUrl} target="_blank">
                            privacy policy
                          </Link>
                        </Typography>
                      }
                      name="privacy_agree_check"
                      checked={getFieldMeta("privacy_agree_check").value}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                    {getFieldMeta("privacy_agree_check").touched &&
                      getFieldMeta("privacy_agree_check").error && (
                        <Typography color="error">
                          {getFieldMeta("privacy_agree_check").error}
                        </Typography>
                      )}
                  </FormGroup>
                  <FormGroup>
                    <FormControlLabel
                      control={<Checkbox />}
                      label="I am over 18 years old"
                      name="age_agree_check"
                      checked={getFieldMeta("age_agree_check").value}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                    {getFieldMeta("age_agree_check").touched &&
                      getFieldMeta("age_agree_check").error && (
                        <Typography color="error">
                          {getFieldMeta("age_agree_check").error}
                        </Typography>
                      )}
                  </FormGroup>
                  <Stack
                    spacing={0.75}
                    sx={{
                      mt: 1,
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 2,
                      p: { xs: 1.25, sm: 1.5 },
                      bgcolor: "background.default",
                    }}
                  >
                    <Typography
                      variant="cellSecondary"
                      sx={{ color: "text.secondary" }}
                    >
                      * For Corporate account; the details must be of a director
                      or an authorized signatory.
                    </Typography>
                    <Typography
                      variant="cellSecondary"
                      sx={{ color: "text.secondary" }}
                    >
                      * The Personal details must be real and according to your
                      ID/Passport/Driving licence.
                    </Typography>
                    <Typography
                      variant="cellSecondary"
                      sx={{ color: "text.secondary" }}
                    >
                      * To verify information obtained from customers all names
                      are screened with the KYC tool and searched through
                      credible sources of commercially or publicly available
                      information.
                    </Typography>
                  </Stack>
                </Stack>
                <LoadingButton
                  fullWidth
                  loading={isSubmitting}
                  variant="contained"
                  type="submit"
                  disabled={!isValid || isSubmitting}
                  sx={{ textTransform: "none", fontWeight: 600, py: 1 }}
                >
                  Next
                </LoadingButton>
                <Button
                  fullWidth
                  variant="text"
                  onClick={() => navigate("/")}
                  sx={{ textTransform: "none" }}
                >
                  Already have an account? Sign In
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </form>
      </Container>
    </Box>
  );
};

export default SignUpWithPassword;
